import React from "react";
import { Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { NavigateFunction } from "react-router-dom";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { handleSendImmixTest } from "./EditCameraUtils/handleSendImmixTest";
import { handleSendDiceTest } from "./EditCameraUtils/handleSendDiceTest";
import { useAppStore } from "../../../appStore";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { usePatriotIgnoreZonesOnAlertPage } from "../../common/PatriotDrawIgnoreZonesGate";
import SelectedStreamDropDown from "./SelectedStreamDropDown";
import {
  AlertReceiverCard,
  ConnectionDetails,
  DetailItem,
  ReceiverSection,
  SecretValue,
} from "./alertDelivery/AlertReceiverCard";
import {
  RECEIVER_CONFIGS,
  RECEIVER_ORDER_AFTER_WEBHOOKS,
  RECEIVER_ORDER_BEFORE_WEBHOOKS,
  ReceiverField,
  ReceiverKey,
  streamAlerts,
  sureviewServerName,
} from "./alertDelivery/receiverConfigs";
import {
  describeReceiver,
  describeWebhook,
} from "./alertDelivery/receiverDescriptions";

// Read-only Alert Settings tab (ENG-2483): one card per alert, headed by what
// that receiver actually gets; credentials and IDs sit in "Connection
// details". Add / delete / test-alert actions are wired as before.

const viewValue = (
  field: ReceiverField,
  alert: any,
  canRevealSecrets: boolean
): React.ReactNode => {
  const value = alert?.[field.key];
  if (field.kind === "secret") {
    return <SecretValue value={value} canReveal={canRevealSecrets} />;
  }
  if (field.kind === "sureviewServer") {
    return value ? sureviewServerName(value) ?? String(value) : "--";
  }
  return value ? String(value) : "--";
};

export const AlertSettingsTab = ({
  immixAlerts,
  setAddImmixAlertDialogOpen,
  setSendingImmixTest,
  setImmixTestSuccessDialogOpen,
  setImmixTestFailDialogOpen,
  setDeleteImmixAlertDialogOpen,
  diceAlerts,
  setAddDiceAlertDialogOpen,
  setSendingDiceTest,
  setDiceTestSuccessDialogOpen,
  setDiceTestFailDialogOpen,
  setDeleteDiceAlertDialogOpen,
  sureviewAlerts,
  setAddSureviewAlertDialogOpen,
  setAddStreamWebhookDialogOpen,
  setEditStreamWebhookDialogOpen,
  sentinelAlerts,
  setAddSentinelAlertDialogOpen,
  boldAlerts,
  setAddBoldAlertDialogOpen,
  patriotAlerts,
  setAddPatriotAlertDialogOpen,
  softguardAlerts,
  setAddSoftguardAlertDialogOpen,
  immixAlertsOnCamera,
  diceAlertsOnCamera,
  sureViewAlertsOnCamera,
  siteInfo,
  webhooksAlertsOnCamera,
  sentinelAlertsOnCamera,
  boldAlertsOnCamera,
  patriotAlertsOnCamera,
  softguardAlertsOnCamera,
  evalinkAlertsOnCamera,
  navigate,
}: {
  immixAlerts?: any;
  setAddImmixAlertDialogOpen?: any;
  setSendingImmixTest?: any;
  setImmixTestSuccessDialogOpen?: any;
  setImmixTestFailDialogOpen?: any;
  setDeleteImmixAlertDialogOpen?: any;
  diceAlerts?: any;
  setAddDiceAlertDialogOpen?: any;
  setSendingDiceTest?: any;
  setDiceTestSuccessDialogOpen?: any;
  setDiceTestFailDialogOpen?: any;
  setDeleteDiceAlertDialogOpen?: any;
  sureviewAlerts: any;
  setAddSureviewAlertDialogOpen: any;
  setAddStreamWebhookDialogOpen: any;
  setEditStreamWebhookDialogOpen: any;
  sentinelAlerts: any;
  setAddSentinelAlertDialogOpen: any;
  boldAlerts: any;
  setAddBoldAlertDialogOpen: any;
  patriotAlerts: any;
  setAddPatriotAlertDialogOpen: any;
  softguardAlerts: any;
  setAddSoftguardAlertDialogOpen: any;
  immixAlertsOnCamera?: boolean;
  diceAlertsOnCamera?: boolean;
  sureViewAlertsOnCamera: boolean;
  siteInfo: any;
  webhooksAlertsOnCamera: boolean;
  sentinelAlertsOnCamera: boolean;
  boldAlertsOnCamera: boolean;
  patriotAlertsOnCamera: boolean;
  softguardAlertsOnCamera: boolean;
  evalinkAlertsOnCamera: boolean;
  navigate: NavigateFunction;
}) => {
  const userInfo = JSON.parse(localStorage.userInfo);
  const permissions = userInfo.permission;
  const { userPermissions } = useAppStore();
  const ignoreZonesOnAlertPage = usePatriotIgnoreZonesOnAlertPage();

  const {
    setDeleteWebhookDialogOpen,
    setWebhookIdToDelete,
    webhooksData,
    cameraStreams,
    cameraStreamsSelectedIndex,
    cameraStreamsObject,
    setSelectedAlertId,
    setSelectedAlertType,
    setDeleteAlertDialogOpen,
    sysaidAlertsOnCamera,
    setAddSysaidAlertDialogOpen,
  } = useEditCameraStore();

  const selectedStream = cameraStreamsObject?.[cameraStreamsSelectedIndex];

  const onCamera: Record<ReceiverKey, boolean | undefined> = {
    immix: immixAlertsOnCamera,
    dice: diceAlertsOnCamera,
    sureview: sureViewAlertsOnCamera,
    sentinel: sentinelAlertsOnCamera,
    bold: boldAlertsOnCamera,
    patriot: patriotAlertsOnCamera,
    softguard: softguardAlertsOnCamera,
    evalink: evalinkAlertsOnCamera,
    sysaid: sysaidAlertsOnCamera,
  };

  // Evalink alerts are created outside this tab, so there is no Add button.
  const openAddDialog: Partial<Record<ReceiverKey, () => void>> = {
    immix: () => setAddImmixAlertDialogOpen(true),
    dice: () => setAddDiceAlertDialogOpen(true),
    sureview: () => setAddSureviewAlertDialogOpen(true),
    sentinel: () => setAddSentinelAlertDialogOpen(true),
    bold: () => setAddBoldAlertDialogOpen(true),
    patriot: () => setAddPatriotAlertDialogOpen(true),
    softguard: () => setAddSoftguardAlertDialogOpen(true),
    sysaid: () => setAddSysaidAlertDialogOpen(true),
  };

  const deleteAlert = (key: ReceiverKey, alert: any) => {
    if (key === "immix") {
      setDeleteImmixAlertDialogOpen(true);
      localStorage.setItem("selectedImmixAlertId", alert.id);
      return;
    }
    if (key === "dice") {
      setDeleteDiceAlertDialogOpen(true);
      localStorage.setItem("selectedDiceAlertId", alert.id);
      return;
    }
    setSelectedAlertId(alert.id);
    setSelectedAlertType(key);
    setDeleteAlertDialogOpen(true);
  };

  const sendTestAlert: Partial<Record<ReceiverKey, (alert: any) => void>> = {
    immix: (alert) => {
      setSendingImmixTest(true);
      handleSendImmixTest(
        alert,
        setSendingImmixTest,
        setImmixTestSuccessDialogOpen,
        setImmixTestFailDialogOpen,
        navigate
      );
    },
    dice: (alert) => {
      setSendingDiceTest(true);
      handleSendDiceTest(
        alert,
        setSendingDiceTest,
        setDiceTestSuccessDialogOpen,
        setDiceTestFailDialogOpen,
        navigate
      );
    },
  };

  const renderReceiver = (key: ReceiverKey) => {
    if (!onCamera[key]) return null;
    const config = RECEIVER_CONFIGS[key];
    const alerts = streamAlerts(selectedStream, config.alertsKey) ?? [];
    const openAdd = openAddDialog[key];
    return (
      <ReceiverSection
        key={key}
        title={`${config.name} Alerts`}
        testId={`alert-receiver-section-${key}`}
        addButton={
          permissions.edit_cameras && openAdd ? (
            <OrangeTextButton
              onClick={openAdd}
              text={`+ Add ${config.name} Alert`}
              disabled={false}
            />
          ) : undefined
        }
      >
        {alerts.map((alert: any, index: number) => {
          const label = `${config.name} Alert ${index + 1}`;
          const { headline, note } = describeReceiver(key, alert, {
            ignoreZonesOnAlertPage,
          });
          const sendTest = sendTestAlert[key];
          return (
            <AlertReceiverCard
              key={alert.id}
              label={label}
              headline={headline}
              note={note}
              testId={`alert-receiver-card-${key}-${alert.id}`}
              actions={
                key === "evalink" ? undefined : (
                  <>
                    {sendTest && (
                      <OrangeTextButton
                        onClick={() => sendTest(alert)}
                        text="Send test alert"
                        disabled={false}
                      />
                    )}
                    <OrangeTextButton
                      onClick={() => deleteAlert(key, alert)}
                      text={
                        <Tooltip title={`Delete ${label}`} placement="top">
                          <DeleteIcon aria-label={`Delete ${label}`} />
                        </Tooltip>
                      }
                      disabled={false}
                    />
                  </>
                )
              }
            >
              <ConnectionDetails idPrefix={`${key}-${alert.id}`}>
                {config.fields.map((field) => (
                  <DetailItem key={field.key} label={field.label}>
                    {viewValue(field, alert, !!userPermissions.view_passwords)}
                  </DetailItem>
                ))}
              </ConnectionDetails>
            </AlertReceiverCard>
          );
        })}
      </ReceiverSection>
    );
  };

  const streamWebhooks: any[] = (webhooksData || []).filter(
    (object: any) =>
      cameraStreams &&
      cameraStreams[cameraStreamsSelectedIndex] &&
      object.stream.id === cameraStreams[cameraStreamsSelectedIndex].id
  );

  return (
    <div className="relative w-full">
      {cameraStreams && cameraStreams.length > 1 && (
        <div className="bg-white">
          <SelectedStreamDropDown />
        </div>
      )}
      {RECEIVER_ORDER_BEFORE_WEBHOOKS.map(renderReceiver)}
      {(webhooksData || siteInfo.webhooks || webhooksAlertsOnCamera) && (
        <ReceiverSection
          title="Webhooks"
          testId="alert-receiver-section-webhooks"
          addButton={
            permissions.edit_cameras ? (
              <OrangeTextButton
                onClick={() => setAddStreamWebhookDialogOpen(true)}
                text="+ Add Stream Webhook"
                disabled={false}
              />
            ) : undefined
          }
        >
          {streamWebhooks.map((object: any, index: number) => {
            const { headline, note } = describeWebhook(object);
            return (
              <AlertReceiverCard
                key={object.id}
                label={`Stream Webhook ${index + 1}`}
                headline={headline}
                note={note}
                testId={`alert-receiver-card-webhook-${object.id}`}
                actions={
                  <>
                    <OrangeTextButton
                      onClick={() => {
                        localStorage.setItem(
                          "selectedWebhook",
                          JSON.stringify(object)
                        );
                        setEditStreamWebhookDialogOpen(true);
                      }}
                      text={
                        <EditIcon
                          fontSize="small"
                          aria-label={`Edit Stream Webhook ${index + 1}`}
                        />
                      }
                      disabled={false}
                    />
                    <OrangeTextButton
                      onClick={() => {
                        setDeleteWebhookDialogOpen(true);
                        setWebhookIdToDelete(object.id);
                      }}
                      text={
                        <DeleteIcon
                          fontSize="small"
                          aria-label={`Delete Stream Webhook ${index + 1}`}
                        />
                      }
                      disabled={false}
                    />
                  </>
                }
              >
                <ConnectionDetails idPrefix={`webhook-${object.id}`}>
                  <DetailItem label="Webhook">
                    {object.webhook?.display_name || "--"}
                  </DetailItem>
                  <DetailItem label="Custom fields">
                    {object.custom_fields
                      ? JSON.stringify(object.custom_fields)
                      : "--"}
                  </DetailItem>
                </ConnectionDetails>
              </AlertReceiverCard>
            );
          })}
        </ReceiverSection>
      )}
      {RECEIVER_ORDER_AFTER_WEBHOOKS.map(renderReceiver)}
    </div>
  );
};
